import type { MouseEvent } from 'react';
import { useI18n, formatDate } from '../../i18n';
import { useLabels } from '../../i18n/labels';
import { TrophyIcon, CalendarIcon, DotIcon, PhoneIcon } from '../shared/Icons';
import { FacebookPill } from '../shared/FacebookPill';
import { hasChampionshipPart, type TournamentType } from '../../utils/tournamentType';

// The pills that the tournament card and the header of the tournament page
// share (operator, 2026-09-30). The two views had their own copies, and the
// copies became different: a different order, different colors, a different
// icon size. #615 (operator, 2026-10-17) — the layout of both views, in
// bands with a line between them:
//   1. the name, the type (TournamentTypeBadge) and the age groups
//      (AgeGroupPills); the dates (TournamentDates) at the right;
//   2. the location and the description;
//   3. the state of the tournament, the registration deadline and the links
//      (TournamentInfoPills), then the roles of the viewer (the card only).

/** `card`: the tournament card. `header`: the header of the tournament page. */
type Where = 'card' | 'header';

// The test ids of the two views keep their names from before.
const testId = (where: Where, name: string) =>
  where === 'card' ? `tournament-card-${name}` : `tournament-${name}-header`;

// A link on the card is inside the link of the card: the tap opens the link,
// not the tournament.
const stop = (e: MouseEvent) => e.stopPropagation();

/** The type of the tournament: the first pill of the card and of the header.
 *  #552 — one line: a long type ("Championship + Open Ring") moves to a new
 *  row as one pill, and does not break inside the pill. */
export function TournamentTypeBadge({ type, where }: { type: TournamentType; where: Where }) {
  const { typeLabel } = useLabels();
  return (
    <span data-testid={testId(where, 'type')} className={`badge gap-1 whitespace-nowrap ${hasChampionshipPart(type) ? 'badge-amber' : 'badge-gray'}`}>
      <TrophyIcon className="w-3.5 h-3.5" />
      {typeLabel(type)}
    </span>
  );
}

interface InfoPillsProps {
  where: Where;
  /** The registration deadline, an instant. Null or absent: no pill. */
  deadline?: string | null;
  facebookUrl?: string | null;
  livestreamUrl?: string | null;
  /** The contact phone. The header only. */
  phone?: string | null;
  className?: string;
}

/** The registration deadline and the links of a tournament. Each pill has an
 *  outline, so this row reads as one group under the age groups and the
 *  type. */
export function TournamentInfoPills({ where, deadline, facebookUrl, livestreamUrl, phone, className = '' }: InfoPillsProps) {
  const { t } = useI18n();
  if (!deadline && !facebookUrl && !livestreamUrl && !phone) return null;
  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${className}`}>
      {/* On a phone the full label wrapped the pill to two lines (36 px):
          the phone gets the short label. */}
      {deadline && (
        <span data-testid={testId(where, 'deadline')} title={t.tournament.regDeadline}
          className="badge gap-1 bg-gray-50 text-gray-600 ring-1 ring-inset ring-gray-200">
          <CalendarIcon className="w-3.5 h-3.5" />
          <span className="sm:hidden">{t.tournament.regDeadlineShort}</span>
          <span className="hidden sm:inline">{t.tournament.regDeadline}</span>
          <span>{formatDate(new Date(deadline), 'dd MMM HH:mm')}</span>
        </span>
      )}
      {facebookUrl && <FacebookPill url={facebookUrl} testId={testId(where, 'facebook')} onClick={stop} />}
      {/* Only the card pulses the live-stream link, as before: the header
          never pulsed it. */}
      {livestreamUrl && (
        <a href={livestreamUrl} target="_blank" rel="noopener noreferrer" data-testid={testId(where, 'livestream')} onClick={stop}
          className={`badge gap-1 bg-red-50 text-red-600 ring-1 ring-inset ring-red-200 hover:bg-red-100 ${where === 'card' ? 'animate-pulse' : ''}`}>
          <DotIcon className="w-3.5 h-3.5" /> {t.tournament.livestream}
        </a>
      )}
      {phone && (
        <a href={`tel:${phone}`} data-testid={testId(where, 'phone')} onClick={stop}
          className="badge gap-1 bg-gray-50 text-gray-700 ring-1 ring-inset ring-gray-200 hover:bg-gray-100">
          <PhoneIcon className="w-3.5 h-3.5" /> {phone}
        </a>
      )}
    </div>
  );
}
